function _EMOTION_STRINGIFIED_CSS_ERROR__() { return "You have tried to stringify object returned from `css` function. It isn't supposed to be used directly (e.g. as value of the `className` prop), but rather handed to emotion so it can handle it (e.g. as value of `css` prop)."; }
import { css, css as _css } from '@emotion/react';
import { useAutocomplete } from '@mui/base/useAutocomplete';
import _ from 'lodash';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import IconChevronDown from '~icons/ci/chevron-down';
import { AnimatedPopover, notchedPopperModifier, Tag } from '@arc/frontend/ui-common';
import { su, theme, typography } from '@arc/frontend/ui-theme';
import { useForwardingRef } from '@arc/frontend/util-common';
import { Checkbox } from '../Checkbox';
import { selectorStyles } from '../Selectors';
import { limitGroupedOptions, limitOptions } from '../use-autocomplete-helpers/limit-options';
const warningMessageStyle = /*#__PURE__*/css(typography.regular, su.textXs, {
  color: theme.colors.text.errorText,
  padding: '0.25rem 0.75rem',
  position: 'sticky',
  marginTop: -4,
  top: -4,
  backgroundColor: theme.colors.background.paper,
  zIndex: 1
}, process.env.NODE_ENV === "production" ? "" : ";label:warningMessageStyle;");
var _ref = process.env.NODE_ENV === "production" ? {
  name: "es4ycv",
  styles: "display:flex;flex-wrap:wrap;align-items:center;justify-content:flex-start;gap:0.25rem;padding-left:0.25rem;padding-top:0.125rem;padding-bottom:0.125rem;min-height:2.5rem;min-width:0"
} : {
  name: "c9wkiq-MultiselectControlled",
  styles: "display:flex;flex-wrap:wrap;align-items:center;justify-content:flex-start;gap:0.25rem;padding-left:0.25rem;padding-top:0.125rem;padding-bottom:0.125rem;min-height:2.5rem;min-width:0;label:MultiselectControlled;",
  toString: _EMOTION_STRINGIFIED_CSS_ERROR__
};
var _ref2 = process.env.NODE_ENV === "production" ? {
  name: "zjik7",
  styles: "display:flex"
} : {
  name: "9513sj-MultiselectControlled",
  styles: "display:flex;label:MultiselectControlled;",
  toString: _EMOTION_STRINGIFIED_CSS_ERROR__
};
/**
 * - Select a single option from a list of options.
 * - The options can be filtered by typing in the input.
 */
export function MultiselectControlled({
  // InputStatus
  error,
  archived,
  readOnly,
  disabled,
  open,
  // InputProps
  placeholder,
  // UseAutocompleteProps
  freeSolo,
  options,
  getOptionLabel = option => '' + option,
  getOptionKey = (i, option) => `${i}-${getOptionLabel(option)}`,
  getOptionDisabled = () => false,
  getOptionGlobal = () => false,
  getOptionColor,
  isOptionEqualToValue = (option, value) => option === value,
  value: valueProp,
  onChange,
  inputValue: propsInputValue,
  onInputChange,
  onClose,
  filterOptions,
  groupBy,
  // other
  popoverRef,
  inputRef: propsInputRef,
  name,
  testid = _.kebabCase(name),
  maxListSize = 50,
  canSearchForMore,
  maxSelectedCount,
  itemName,
  popoverStyle,
  isLoading,
  styles,
  slotProps,
  renderTag = defaultRenderTag,
  renderGroup = defaultRenderGroup,
  renderOption = defaultRenderOption,
  emptyMessage,
  dropdownTopContainer,
  popperProps = MultiselectBasePopperProps,
  endAdornment,
  clearOnBlur = false
}) {
  const {
    t
  } = useTranslation(['common', 'validations']);
  const {
    getRootProps,
    getInputProps,
    getListboxProps,
    getOptionProps,
    groupedOptions,
    anchorEl,
    setAnchorEl,
    popupOpen,
    getPopupIndicatorProps,
    getTagProps,
    inputValue,
    value
  } = useAutocomplete({
    // Always close on normal select. The user can use the checkboxes to select
    // multiple at once without closing the popover.
    disableCloseOnSelect: false,
    openOnFocus: true,
    clearOnBlur,
    multiple: true,
    open,
    freeSolo,
    filterSelectedOptions: false,
    options,
    getOptionLabel: (...args) => getOptionLabel(...args) ?? '',
    getOptionDisabled: option => canSelectItem || getOptionDisabled(option),
    isOptionEqualToValue,
    value: valueProp,
    onChange: (_event, newValue) => {
      onChange(maxSelectedCount ? newValue.slice(0, maxSelectedCount) : newValue);
    },
    disabled,
    readOnly,
    inputValue: propsInputValue,
    onInputChange,
    onClose,
    filterOptions,
    groupBy
  });
  const canSelectItem = maxSelectedCount ? value.length >= maxSelectedCount : false;
  const renderLimitedNotice = () => <li css={selectorStyles.emptyListItem}>{t('typeForMoreResults')}</li>;
  const getOptionColorInternal = option => getOptionColor?.(option) ?? theme.colors.background.tag.blue;
  const renderOptionInternal = (option, index) => {
    const selected = value.some(v => isOptionEqualToValue(option, v));
    const key = getOptionKey(index, option);
    const optionProps = getOptionProps({
      option,
      index
    });
    const disabled_ = optionProps['aria-disabled'] === true;
    return renderOption({
      key,
      optionProps,
      checkboxProps: {
        type: 'checkbox',
        checked: selected,
        // The checkbox is inside the option, but we do not want the option's
        // `onClick` to be called because the checkbox should have different
        // behavior. When the checkbox is clicked, we do not want to close the
        // popover.
        onClick: e => {
          e.stopPropagation();
        },
        onChange: e => {
          const newValue = e.target.checked ? [...value, option] : value.filter(v => !isOptionEqualToValue(option, v));
          onChange(maxSelectedCount ? newValue.slice(0, maxSelectedCount) : newValue);
        },
        disabled: optionProps['aria-disabled'] === true
      },
      label: getOptionLabel(option),
      color: getOptionColorInternal(option),
      global: getOptionGlobal(option),
      option,
      selected,
      inputValue,
      disabled: disabled_
    });
  };
  const renderOptions = options_ => options_.map((option, index) => renderOptionInternal(option, index));
  const renderGroups = groups => groups.map(({
    key,
    group,
    options: options_,
    index
  }) => renderGroup({
    key,
    group,
    children: options_.map((option2, index2) => renderOptionInternal(option2, index + index2))
  }));
  const inputProps = getInputProps();
  const inputRef = useForwardingRef(null, [inputProps.ref, propsInputRef]);
  return <div {...getRootProps()}>
			<div ref={setAnchorEl} css={[selectorStyles.multiselectContainer({
      error: !!error,
      disabled,
      archived,
      readOnly
    }), "padding:0;display:grid;grid-template-columns:1fr auto;align-items:stretch;justify-content:stretch;gap:0;" + (process.env.NODE_ENV === "production" ? "" : ";label:MultiselectControlled;")]}>
				<div data-testid={`${testid}-selected-items`} css={_ref}>
					{value.map((tag, index) => {
          const {
            onDelete,
            ...tagProps
          } = getTagProps({
            index
          });
          return renderTag({
            tagProps,
            onDelete: disabled || readOnly ? undefined : onDelete,
            label: getOptionLabel(tag),
            color: getOptionColorInternal(tag),
            global: getOptionGlobal(tag),
            option: tag,
            testid
          });
        })}
					<input {...inputProps} ref={inputRef} placeholder={value?.length ? undefined : placeholder} css={[selectorStyles.multiselectInput({
          disabled,
          archived,
          readOnly
        }),
        // Fade the input value when the popup is closed so that it does not
        // look like the search text is part of the selected value.
        popupOpen ? undefined : {
          // Same color as input placeholders.
          color: theme.colors.text.hint
        }, styles?.input, process.env.NODE_ENV === "production" ? "" : ";label:MultiselectControlled;"]} disabled={disabled} readOnly={readOnly} data-testid={`${testid}-input`} {...slotProps?.input} />
				</div>
				<div css={_ref2}>
					{readOnly ? null : <button {...getPopupIndicatorProps()} type="button" disabled={disabled} css={/*#__PURE__*/_css({
          color: theme.colors.input.adornment,
          cursor: 'pointer',
          width: '2.5rem',
          height: '2.5rem',
          // Center the icon in the button.
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }, process.env.NODE_ENV === "production" ? "" : ";label:MultiselectControlled;")} data-testid={`${testid}-button`}>
							<IconChevronDown css={[disabled && /*#__PURE__*/css({
            color: theme.colors.text.disabled
          }, process.env.NODE_ENV === "production" ? "" : ";label:MultiselectControlled;"), popupOpen && {
            rotate: '180deg'
          }, process.env.NODE_ENV === "production" ? "" : ";label:MultiselectControlled;"]} width={20} height={20} />
						</button>}
					{endAdornment}
				</div>
			</div>
			<AnimatedPopover ref={popoverRef} anchorEl={anchorEl} open={popupOpen} PopperProps={popperProps} css={[selectorStyles.popover(), popoverStyle, process.env.NODE_ENV === "production" ? "" : ";label:MultiselectControlled;"]} data-testid={`${testid}-popover`}>
				<ul {...getListboxProps()} css={[selectorStyles.list, "max-height:40vh;overflow:auto;", styles?.listbox, process.env.NODE_ENV === "production" ? "" : ";label:MultiselectControlled;"]}>
					{dropdownTopContainer}

					{maxSelectedCount && value.length >= maxSelectedCount ? <div css={warningMessageStyle}>
							{t('validations:Max Items', {
            maxCount: maxSelectedCount,
            itemName
          })}
						</div> : null}

					{groupedOptions.length <= 0 ? isLoading ? <li css={[selectorStyles.emptyListItem, typography.emptySmall, process.env.NODE_ENV === "production" ? "" : ";label:MultiselectControlled;"]}>
								{t('loading')}
							</li> : <li css={selectorStyles.emptyListItem}>
								{emptyMessage ?? t('noResults')}
							</li> : groupBy ? (() => {
          const {
            limitedGroups,
            limited
          } = limitGroupedOptions(groupedOptions, maxListSize);
          return <>
									{renderGroups(limitedGroups)}
									{limited || canSearchForMore ? renderLimitedNotice() : null}
								</>;
        })() : (() => {
          const {
            limitedOptions,
            limited
          } = limitOptions(groupedOptions, maxListSize);
          return <>
									{renderOptions(limitedOptions)}
									{limited || canSearchForMore ? renderLimitedNotice() : null}
								</>;
        })()}
				</ul>
			</AnimatedPopover>
		</div>;
}
export const MultiselectBasePopperProps = {
  placement: 'bottom-start',
  modifiers: [{
    name: 'offset',
    options: {
      offset: [0, -2]
    }
  }, notchedPopperModifier({
    notchDepth: 1
  })]
};
function defaultRenderTag({
  tagProps: {
    key,
    ...tagProps
  },
  onDelete,
  label,
  color,
  global,
  testid
}) {
  return <Tag key={key} {...tagProps} css={onDelete === undefined ? undefined : {
    cursor: 'pointer'
  }} text={label} color={color} global={global} size="clickable" removable={onDelete !== undefined} onClick={onDelete} data-testid={`${testid}-selected-item`} />;
}
var _ref3 = process.env.NODE_ENV === "production" ? {
  name: "8m89z8",
  styles: "& > li{padding-left:1.5rem;}"
} : {
  name: "yovgo4-defaultRenderGroup",
  styles: "& > li{padding-left:1.5rem;};label:defaultRenderGroup;",
  toString: _EMOTION_STRINGIFIED_CSS_ERROR__
};
function defaultRenderGroup({
  key,
  group,
  children
}) {
  return <li key={key}>
			<div css={[typography.regular, {
      color: theme.colors.text.secondary,
      padding: '0.5rem 0.75rem'
    }, process.env.NODE_ENV === "production" ? "" : ";label:defaultRenderGroup;"]}>
				{group}
			</div>
			<ul css={_ref3}>{children}</ul>
		</li>;
}
function defaultRenderOption({
  key,
  optionProps: {
    key: optionKey,
    ...optionProps
  },
  checkboxProps: {
    key: checkboxKey,
    ...checkboxProps
  },
  label,
  color,
  global,
  inputValue,
  disabled
}) {
  return <li key={key ?? optionKey} {...optionProps} onClick={e => {
    if (!disabled) {
      optionProps.onClick?.(e);
    }
  }} css={[selectorStyles.listItem, "display:flex;padding-left:0;" + (process.env.NODE_ENV === "production" ? "" : ";label:defaultRenderOption;")]}>
			<Checkbox key={checkboxKey} {...checkboxProps} containerStyle={{
      width: '2.5rem',
      height: '2rem'
    }} />

			<Tag text={label} color={color} global={global} searchTerm={inputValue} size="clickable" />
		</li>;
}

/**
 * This wrapper component makes sure input value is not cleared until the dropdown is closed
 * and input value is not cleared out when selecting options using checkboxes, but it does not take
 * inputValue as a prop, so it is not possible to control the input value from outside.
 * If you need to control the input value from outside, use `MultiselectControlled` instead.
 */
export function Multiselect({
  onClose,
  onInputChange,
  ...props
}) {
  const [stateInputValue, setStateInputValue] = useState('');
  return <MultiselectControlled {...props} inputValue={stateInputValue} onClose={(event, reason) => {
    onClose?.(event, reason);
    setStateInputValue('');
  }} onInputChange={(e, value, reason) => {
    onInputChange?.(e, value, reason);
    setStateInputValue(value);
  }} />;
}